import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useSaveActionsContext } from "context/save";

import useMaterialCategoryApi from "hooks/api/private/useMaterialCategoryApi";

import { LanguageEnum } from "types/api/commons";
import { MaterialCategoryBody, MaterialCategoryDetails } from "types/api/materialCategory";
import { IndoorOrOutdoor } from "types/enums";

export type MaterialCategoryBodyForm = {
    type: IndoorOrOutdoor;
    graphical_order: number | null;
    text: {
        [x in LanguageEnum]: {
            name: string;
        };
    };
};

export type MaterialCategoryFormHook = {
    formHook: FormHookType<MaterialCategoryBodyForm>;
};

function useMaterialCategoryForm({
    materialCategoryId,
}: {
    materialCategoryId?: string;
}): MaterialCategoryFormHook {
    const { createMaterialCategory, updateMaterialCategory, getOneMaterialCategory } =
        useMaterialCategoryApi();

    const { addSaveAction } = useSaveActionsContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                type: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                graphical_order: {
                    type: "simple",
                    validators: [],
                },
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            name: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                        },
                    },
                },
            },
        };
    }, []);

    const initialValues: MaterialCategoryBodyForm = React.useMemo(() => {
        return {
            type: IndoorOrOutdoor.indoor,
            graphical_order: null,
            text: {
                it: { name: "" },
                en: { name: "" },
            },
        };
    }, []);

    const formHook = useForm<MaterialCategoryBodyForm>(schema, initialValues);

    const { showAllError, setForm, isValid, form } = formHook;

    const [details, setDetails] = React.useState<MaterialCategoryDetails | undefined>(undefined);

    React.useEffect(() => {
        if (materialCategoryId) {
            const [apiCall, abortController] = getOneMaterialCategory({ id: materialCategoryId });

            apiCall.then(setDetails);

            return () => abortController.abort();
        }
    }, [getOneMaterialCategory, materialCategoryId]);

    React.useEffect(() => {
        if (details) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.type = details.type as IndoorOrOutdoor;
                newForm.graphical_order = details.graphical_order;
                newForm.text.it.name = details.text.it.name;
                newForm.text.en.name = details.text.en.name;

                return newForm;
            });
        }
    }, [setForm, details]);

    const body: MaterialCategoryBody = React.useMemo(() => {
        return {
            type: form.type,
            graphical_order: form.graphical_order ?? 0,
            text: {
                it: { name: form.text.it.name },
                en: { name: form.text.en.name },
            },
        };
    }, [form]);

    React.useEffect(() => {
        addSaveAction("useMaterialCategoryForm", () => {
            if (isValid) {
                const promises: Promise<unknown>[] = [];

                if (materialCategoryId) {
                    const [apiCall] = updateMaterialCategory({ id: materialCategoryId }, body);
                    promises.push(apiCall);
                } else {
                    const [apiCall] = createMaterialCategory(body);
                    promises.push(apiCall);
                }

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        addSaveAction,
        showAllError,
        createMaterialCategory,
        updateMaterialCategory,
        body,
        materialCategoryId,
        isValid,
    ]);

    return React.useMemo(
        (): MaterialCategoryFormHook => ({
            formHook,
        }),
        [formHook],
    );
}

export default useMaterialCategoryForm;
